/**
 * HardCopyTrackingList - Filterable table of hard-copy tracking records.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Search, Filter, Plus, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { TrackingNumberBadge } from './TrackingNumberBadge';
import { HardCopyTrackingDialog } from './HardCopyTrackingDialog';
import { HardCopyTrackingPanel } from './HardCopyTrackingPanel';
import hardCopyTrackingAPI from '@/services/api/hardCopyTracking';
import {
  TRACKING_STATUS_CONFIG,
  type HardCopyTrackingRecord,
  type HardCopyTrackingStatus,
} from '@/types/hardCopyTracking';

interface HardCopyTrackingListProps {
  /** Filter by member */
  memberId?: number;
  memberName?: string;
  /** Filter by enrollment */
  enrollmentId?: number;
  /** Filter by program */
  programId?: number;
  /** For create action: prefill target info */
  targetType?: 'member' | 'staff';
  targetId?: number;
  /** Show create button */
  showCreate?: boolean;
  /** Compact mode for embedding */
  compact?: boolean;
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

export function HardCopyTrackingList({
  memberId,
  memberName,
  enrollmentId,
  programId,
  targetType = 'member',
  targetId,
  showCreate = true,
  compact = false,
}: HardCopyTrackingListProps) {
  const [records, setRecords] = useState<HardCopyTrackingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState<HardCopyTrackingRecord | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page, page_size: 25 };
      if (memberId) params.member_id = memberId;
      if (enrollmentId) params.enrollment_id = enrollmentId;
      if (programId) params.program_id = programId;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.tracking_number = searchQuery;

      const data = await hardCopyTrackingAPI.list(params);
      setRecords(data);
    } catch (e) {
      console.error('Error fetching tracking records:', e);
    } finally {
      setLoading(false);
    }
  }, [memberId, enrollmentId, programId, statusFilter, searchQuery, page]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleCreateComplete = () => {
    fetchRecords();
  };

  // Detail view
  if (selectedRecord) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(null)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to list
        </Button>
        <HardCopyTrackingPanel
          trackingId={selectedRecord.id}
          onUpdate={(updated) => {
            setSelectedRecord(updated);
            fetchRecords();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {!compact && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Hard-Copy Tracking
            <Badge variant="secondary">{records.length}</Badge>
          </h3>
          {showCreate && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> New Tracking
            </Button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search tracking number..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-52">
            <Filter className="w-3.5 h-3.5 mr-2" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="generated">Generated</SelectItem>
            <SelectItem value="printed">Printed</SelectItem>
            <SelectItem value="provided_for_signature">Provided for Signature</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="uploaded_signed_copy">Uploaded</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        {compact && showCreate && (
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Track
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No hard-copy tracking records found.</p>
            {showCreate && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create First Tracking Record
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking #</TableHead>
                {!memberId && <TableHead>Member</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Generated</TableHead>
                <TableHead>Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((rec) => {
                const statusCfg = TRACKING_STATUS_CONFIG[rec.status];
                return (
                  <TableRow
                    key={rec.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedRecord(rec)}
                  >
                    <TableCell>
                      <TrackingNumberBadge
                        trackingNumber={rec.tracking_number}
                        status={rec.status}
                        size="sm"
                        showCopy={false}
                      />
                    </TableCell>
                    {!memberId && (
                      <TableCell className="text-sm">
                        {rec.target_name || `${rec.target_type} #${rec.target_id}`}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge className={statusCfg.color} size="sm">
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{rec.program_name || '-'}</TableCell>
                    <TableCell className="text-sm">{formatDate(rec.generated_at)}</TableCell>
                    <TableCell className="text-sm">{formatDate(rec.updated_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={records.length < 25}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <HardCopyTrackingDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        action="create"
        createDefaults={{
          target_type: targetType,
          target_id: targetId || memberId,
          program_id: programId,
          enrollment_id: enrollmentId,
        }}
        onComplete={handleCreateComplete}
      />
    </div>
  );
}
